import { Controller } from '@hotwired/stimulus';
import Papa from 'papaparse';
import xlsx from 'xlsx';
import type ProgressController from './progress_controller';
import type VaultController from './vault_controller';
import { saveBlob } from '../save_blob';

declare global {
  interface Window {
    vault: VaultController;
  }
}

interface CSV {
  data: string[];
  errors: string[];
  meta: {
    delimiter: string;
    linebreak: string;
    aborted: boolean;
    cursor: number;
    renamedHeaders: string[];
    truncated: boolean;
  };
}

// Files larger than this can't be reliably converted to XLSX in the browser because the
// whole workbook must be materialized in the JS heap (raw CSV + parsed array + SheetJS
// workbook ~= 3x). Mirrors JobTracking::MAX_XLSX_BYTES on the Ruby side.
const MAX_XLSX_BYTES = 50 * 1024 * 1024;

class CountStream {
  readable;
  writable;
  constructor(context: any, contentLength: string | null) {
    const { readable, writable } = new TransformStream();
    const gen = readable.getReader();
    this.readable = new ReadableStream({
      start(controller) {
        this.currentContentLength = 0;
        this.contentLength = Number(contentLength);
        this.context = context;
      },
      async pull(controller) {
        const { done, value }: { done: boolean; value: Uint8Array } = await gen.read();
        if (done) return controller.close();

        this.currentContentLength += value.length;

        if (this.context.hasProgressOutlet) {
          this.context.progressOutlet?.updateText(
            `Receiving Data ${this.context.formatBytes(this.currentContentLength)}/${this.context.formatBytes(this.contentLength)}`
          );
        }

        controller.enqueue(value);
      },
    });
    this.writable = writable;
  }
}

export default class extends Controller {
  static values = {
    url: String,
    filename: String,
    lines: Number,
    formId: String,
    tooLargeMessage: String,
    somethingWentWrongMessage: String,
  };
  static targets = ['button'];
  static outlets = ['progress'];

  started: boolean = false;
  urlValue!: string;
  linesValue!: number;
  buttonTarget!: HTMLButtonElement;
  filenameValue!: string;
  formIdValue!: string;
  hasFormIdValue!: boolean;
  tooLargeMessageValue!: string;
  hasTooLargeMessageValue!: boolean;
  somethingWentWrongMessageValue!: string;
  hasSomethingWentWrongMessageValue!: boolean;
  progressOutlet!: ProgressController;
  hasProgressOutlet!: boolean;
  payload: FormData | null = null;

  initialize() {}

  async downloadDecryptedXlsx() {
    await this.download(true, 'xlsx');
  }

  async downloadDecryptedCsv() {
    await this.download(true, 'csv');
  }

  async downloadEncryptedXlsx() {
    await this.download(false, 'xlsx');
  }

  async downloadEncryptedCsv() {
    await this.download(false, 'csv');
  }

  formatBytes(bytes: number, decimals = 2) {
    if (!+bytes) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
  }

  headers: string[] = [];
  encryption_indices: number[] = [];
  current_line: number = 1;
  encryptedBlobRegex = /encrypted_blob\/(\d+)/;

  // Build a preview URL for encrypted blobs so the UI can show a decrypted image when unlocked.
  previewUrlForEncryptedBlob(value: string): string | null {
    const match = value.match(this.encryptedBlobRegex);
    if (!match) return null;
    return `${window.location.origin}/encrypted_blob/${match[1]}?preview=true`;
  }

  // we decrypt one row after the next and the first call should be the header
  // the Promise.all call should resolve each promise after the other
  // and the header should therefore be the first one
  // we are in the main Javascript thread and nothing should resolve in parallel
  async decryptRow(fields: string[]): Promise<string[]> {
    if (this.headers.length === 0) {
      this.headers = fields;

      for (const [index, header] of this.headers.entries()) {
        if (header.endsWith('_e')) {
          this.encryption_indices.push(index);
          this.headers[index] = this.headers[index]?.slice(0, -2);
        }
      }

      return this.headers;
    }

    for (const index of this.encryption_indices) {
      // Never widen a ragged row: assigning past the end pads it with holes, which unparse
      // writes as a long run of empty columns welded onto the previous row.
      if (index >= fields.length) continue;

      const value = fields[index];
      // Encrypted blob links are not decryptable strings; when the vault is open we swap them
      // for preview URLs and skip decryption, otherwise we decrypt normal encrypted fields.
      if (typeof value === 'string' && value.match(this.encryptedBlobRegex)) {
        if (window.vault?.is_open?.()) {
          const previewUrl = this.previewUrlForEncryptedBlob(value);
          if (previewUrl) {
            fields[index] = previewUrl;
          }
        }
        continue;
      }
      fields[index] = await window.vault.decryptMessage(value);
    }

    if (this.hasProgressOutlet && this.current_line % 100 === 0) {
      this.progressOutlet?.updateText(`Decrypting Data ${this.current_line}/${this.linesValue}`);
    }

    this.current_line += 1;

    return fields;
  }

  async leaveRow(fields: string[]): Promise<string[]> {
    return Promise.resolve(fields);
  }

  // this stream transformer will parse the already received lines
  // of the csv and apply the provided editRow function
  csvTransformer(editRow: (row: string[]) => Promise<string[]>): TransformStream {
    const parse_chunk = async (
      chunk: string,
      editRow: (fields: string[]) => Promise<string[]>
    ): Promise<string[][]> => {
      const csv: Papa.ParseResult<string[]> = Papa.parse(chunk);

      const decrypted_csv = await Promise.all(
        csv.data.map((value: string[]): Promise<string[]> => {
          return editRow(value);
        })
      );
      return decrypted_csv;
    };

    return new TransformStream({
      start() {
        this._leftover = '';
      },

      async transform(chunk, controller) {
        const text = this._leftover + new TextDecoder().decode(chunk);
        const last_newline = text.lastIndexOf('\n');

        // if we have no newline, it's a super small chunk
        if (last_newline === -1) {
          this._leftover = text;
        } else {
          const rows = text.slice(0, last_newline);
          // Drop the newline rather than carrying it: a leftover starting with "\n" parses as an
          // empty leading row. And unparse writes no trailing separator, so without the appended
          // one the next chunk's first row is concatenated onto this chunk's last row.
          this._leftover = text.slice(last_newline + 1);

          controller.enqueue(
            new TextEncoder().encode(`${Papa.unparse(await parse_chunk(rows, editRow))}\r\n`)
          );
        }
      },

      async flush(controller) {
        if (this._leftover) {
          controller.enqueue(
            new TextEncoder().encode(Papa.unparse(await parse_chunk(this._leftover, editRow)))
          );
        }
      },
    });
  }

  async downloadFileEditRows(
    url: string,
    fileName: string,
    editRow: (row: string[]) => Promise<string[]>
  ) {
    // we can't laverage any browser features in the xlsx path
    // so it's the always the same, we could maybe build the excel
    // file while parsing the stream, but in the end the whole
    // file would sit in the ram anyways...
    try {
      if (fileName.endsWith('xlsx')) {
        const request = await this.fetchRequest(url);
        if (await this.bailIfFailed(request)) return;

        // Prioritize filename the server generated
        fileName = this.filenameFromResponse(request, fileName);

        const length = request.headers.get('content-length');

        if (length && Number(length) > MAX_XLSX_BYTES) {
          window.dispatchEvent(new CustomEvent('close-modal'));
          this.started = false;
          window.alert(
            this.hasTooLargeMessageValue
              ? this.tooLargeMessageValue
              : 'File is too large to convert to XLSX in the browser (max 50 MB). Please download as CSV instead.'
          );
          return;
        }

        const request_stream = request.body
          ?.pipeThrough(new CountStream(this, length))
          .pipeThrough(this.csvTransformer(editRow));

        const csv = Papa.parse(await new Response(request_stream).text());
        const wb = xlsx.utils.book_new();
        const ws = xlsx.utils.aoa_to_sheet(csv.data, { dense: true });

        xlsx.utils.book_append_sheet(wb, ws, 'Export');

        // compression: true is intentionally omitted — DEFLATE runs after the workbook
        // is already fully built in memory, adding another ~full-size pass to the JS
        // heap. Without it peak memory drops from ~4× to ~3× the CSV size, which
        // meaningfully raises the row limit before the browser tab runs out of memory.
        xlsx.writeFileXLSX(wb, fileName, { type: 'file' });

        this.closeModal();
        return;
      }

      // declared out here so the log below can see it, whichever branch ran
      let request: Response;

      if (window.showSaveFilePicker) {
        // chrome based browser path
        const newFile = await window.showSaveFilePicker({
          suggestedName: fileName,
        });
        const writableStream = await newFile.createWritable();
        request = await this.fetchRequest(url);
        if (await this.bailIfFailed(request)) return;

        const length = request.headers.get('content-length');

        request?.body
          ?.pipeThrough(new CountStream(this, length))
          .pipeThrough(this.csvTransformer(editRow))
          .pipeTo(writableStream);
      } else {
        request = await this.fetchRequest(url);
        if (await this.bailIfFailed(request)) return;

        const length = request.headers.get('content-length');
        const request_stream = request.body
          ?.pipeThrough(new CountStream(this, length))
          .pipeThrough(this.csvTransformer(editRow));

        saveBlob(await new Response(request_stream).blob(), fileName);
      }

      this.closeModal();
      console.log('FETCH', request.status, request.url, request.redirected);
    } catch (e) {
      console.log('FETCH_ERROR', e?.message || e);
      throw e;
    }
  }

  private filenameFromResponse(response: Response, fallback: string): string {
    const disposition = response.headers.get('content-disposition');
    if (!disposition) return fallback;

    const match = disposition.match(/filename="?([^";]+)"?/i);
    if (!match || !match[1]) return fallback;

    let filename = match[1];
    try {
      filename = decodeURIComponent(filename);
    } catch {
      // leave it percent-encoded rather than fail the whole download over a cosmetic name
    }

    return filename.replace(/\.csv$/i, '.xlsx');
  }

  private async bailIfFailed(request: Response): Promise<boolean> {
    if (request.ok && !request.redirected) return false;

    window.dispatchEvent(new CustomEvent('close-modal'));
    this.started = false;

    if (request.redirected) {
      window.alert(
        this.hasSomethingWentWrongMessageValue
          ? this.somethingWentWrongMessageValue
          : 'Something went wrong, please refresh and try again.'
      );
    } else if (this.payload) {
      // A GET via location.href would 404 against a POST-only route (e.g.
      // report_reviewer_activity_submit) - resubmit with the same method and body so the browser
      // lands on the real error response instead of a routing error.
      this.resubmitAsForm(request.url, this.payload);
    } else {
      window.location.href = request.url;
    }

    return true;
  }

  private resubmitAsForm(url: string, payload: FormData) {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = url;

    for (const [name, value] of payload.entries()) {
      if (typeof value !== 'string') continue; // no File fields expected in these forms

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
  }

  private fetchRequest(url: string): Promise<Response> {
    if (this.payload) {
      return fetch(url, {
        method: 'POST',
        body: this.payload,
        headers: { ...this.getCsrfToken(), 'X-Requested-With': 'XMLHttpRequest' },
      });
    }

    return fetch(url, { method: 'GET', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
  }

  getCsrfToken() {
    const el = document.querySelector('meta[name="csrf-token"]');
    return el ? { 'X-CSRF-Token': el.getAttribute('content')! } : {};
  }

  closeModal() {
    if (this.hasProgressOutlet) {
      window.dispatchEvent(new CustomEvent('open-modal'));
      this.progressOutlet?.updateText('Saving File');
    } else {
      console.log('Saving File');
    }

    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('close-modal'));
      this.started = false;
    }, 2000);
  }

  async download(decrypt: boolean, type: string) {
    if (this.started) {
      return;
    }

    if (decrypt && !window.vault.is_open()) {
      window.vault.openModal(() => {
        this.download(decrypt, type);
      });
      return;
    }

    this.started = true;

    if (this.hasProgressOutlet) {
      window.dispatchEvent(new CustomEvent('open-modal'));
      this.progressOutlet?.updateText('Downloading Data');
    } else {
      console.log('Downloading Data');
    }

    let editRow: (fields: string[]) => Promise<string[]>;

    if (decrypt) {
      editRow = this.decryptRow.bind(this);
    } else {
      editRow = this.leaveRow;
    }

    if (this.hasFormIdValue) {
      const form = document.getElementById(this.formIdValue);
      if (form) this.payload = new FormData(form as HTMLFormElement);
    }

    await this.downloadFileEditRows(this.urlValue, this.filenameValue, editRow);
  }
}
